import { formatarMedicamentoParaReceita } from 'data/medicamentos';
import { format } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { extrairCorpoReceita } from 'pages/receita/extrairCorpoReceita';
import { ehReceitaLivre, textoParaReceitaLivre } from 'pages/receita/receitaLivre';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectDoctor } from 'redux/features/auth/authSlice';
import api from 'services/api';
import { deleteReceita } from 'services/receita';
import useSWR from 'swr';
import { isInfrastructureError } from 'utils/apiErrors';
import { mensagemErroExclusao, podeExcluirReceita } from 'utils/gravacao';

const USO_TIPO_LABEL = {
  oral: 'USO ORAL CONTÍNUO',
  subcutaneo: 'USO SUBCUTÂNEO',
  externo: 'USO EXTERNO CONTÍNUO',
};

/**
 * Hook compartilhado com toda a lógica de negócio de receitas.
 *
 * @param {object} params
 * @param {string|number|null} params.codPac   - Código do paciente
 * @param {object|null}        params.consulta  - Prontuário/consulta vinculado (opcional)
 * @param {object|null}        params.prontuario - Prontuário da consulta, quando a receita nasce no prontuário
 * @param {boolean}            params.ativo     - Controla se o SWR deve disparar (false = lazy)
 */
export function useReceitas({ codPac, consulta = null, prontuario = null, ativo = true }) {
  const history = useHistory();
  const medico = useSelector(selectDoctor);
  const canMutate = !!medico;
  const descricaoValueRef = useRef('');
  const [dirty, setDirty] = useState(false);
  const [codReceitaSelecionada, setCodReceitaSelecionada] = useState(null);
  const [descricao, setDescricao] = useState('');
  const [tipoReceita, setTipoReceita] = useState('Normal');
  const [usoTipo, setUsoTipo] = useState('oral');
  const [mutating, setMutating] = useState(false);

  // Fonte de dados do paciente — prontuário da consulta ou o objeto passado pela página de receitas
  const dadosPaciente = prontuario || consulta;

  const swrKey = ativo && codPac ? ['receitas-paciente', codPac] : null;
  const {
    data: receitas = [],
    error,
    isLoading,
    mutate,
  } = useSWR(
    swrKey,
    ([, cod]) =>
      api
        .get(`paciente/${cod}/receitas`)
        .then((response) => (Array.isArray(response.data) ? response.data : [])),
    { shouldRetryOnError: false }
  );

  const loading = isLoading || mutating;
  const erroInfraestrutura = isInfrastructureError(error);
  const temReceitas = receitas.length > 0;

  const receitaSelecionada = useMemo(() => {
    if (!receitas.length) return null;
    if (codReceitaSelecionada == null) return receitas[0];
    return receitas.find((r) => r.CODRECEITA === codReceitaSelecionada) || receitas[0];
  }, [receitas, codReceitaSelecionada]);

  const alterarTipoReceita = (tipo) => {
    if (ehReceitaLivre(tipo) && !ehReceitaLivre(tipoReceita)) {
      const corpo = textoParaReceitaLivre(descricaoValueRef.current);
      if (corpo !== descricaoValueRef.current) {
        descricaoValueRef.current = corpo;
        setDescricao(corpo);
        setDirty(true);
      }
    }
    setTipoReceita(tipo);
  };

  const isDefinitivo = receitaSelecionada?.GRAVACAO === 'DEFINITIVO';
  const isEditLocked = isDefinitivo || !canMutate;

  const numeraReceita =
    String(descricao || '')
      .split('\n')
      .filter((l) => l.match(/^\d+[\s\)-]/)).length + 1;

  // Sincroniza o formulário ao mudar a receita selecionada
  useEffect(() => {
    if (receitaSelecionada) {
      const texto = receitaSelecionada.TEXTO || '';
      descricaoValueRef.current = texto;
      setDescricao(texto);
      setDirty(false);
      setTipoReceita(receitaSelecionada.TIPORECEITA || 'Normal');
    }
  }, [receitaSelecionada]);

  // Cobre aliases de campo de ambos os contextos (página vs modal)
  const montarEnderecoPaciente = (pac = {}) => {
    const endereco = pac.ENDRESPAC || pac.ENDERECO || '';
    const complemento = pac.SETORPAC || pac.COMPLEMENTO || '';
    const bairro = pac.BAIRROPAC || pac.BAIRRO || '';
    const cidade = pac.CIDADEPAC || pac.CIDADE || '';
    const estado = pac.ESTADOPAC || pac.UF || '';

    const blocoPrincipal = [endereco, complemento].filter(Boolean).join(', ');
    let blocoLocalidade = [bairro, cidade].filter(Boolean).join(' - ');

    if (estado) {
      blocoLocalidade = blocoLocalidade ? `${blocoLocalidade}/${estado}` : estado;
    }

    return [blocoPrincipal, blocoLocalidade].filter(Boolean).join(' - ');
  };

  const atualizarDescricao = (value) => {
    if (!canMutate || isEditLocked) return;
    descricaoValueRef.current = value;
    setDescricao(value);
    setDirty(true);
  };

  const payloadUpdate = (gravacao) => ({
    tipoRec: tipoReceita,
    descricao: descricaoValueRef.current,
    gravacao,
  });

  const salvarDescricaoPendente = async () => {
    if (!canMutate || !dirty || isDefinitivo || !receitaSelecionada) return;

    try {
      await api.put(`receita/${receitaSelecionada.CODRECEITA}`, payloadUpdate('PARCIAL'));
      setDirty(false);
      await mutate();
    } catch {
      toast.error('Erro ao salvar automaticamente.');
    }
  };

  const montarCabecalho = (pac) => {
    const dataExtenso = format(new Date(), "dd 'de' MMMM 'de' yyyy", { locale: pt });
    const enderecoPac = montarEnderecoPaciente(pac);
    const tipoUsoLabel = USO_TIPO_LABEL[usoTipo] || USO_TIPO_LABEL.externo;

    return (
      `Marília/SP, ${dataExtenso}.\n\n` +
      `Nome: ${pac?.NOMEPAC || pac?.NOME || ''} - CPF: ${pac?.CPFPAC || pac?.CPF || ''}\n` +
      `Endereço: ${enderecoPac}\n\n${tipoUsoLabel}\n\n\n`
    );
  };

  const criarReceita = async (payload, mensagemSucesso, mensagemErro) => {
    try {
      setMutating(true);
      await api.post('receita', payload);
      toast.success(mensagemSucesso);
      const atualizadas = await mutate();
      setCodReceitaSelecionada(
        Array.isArray(atualizadas) && atualizadas[0] ? atualizadas[0].CODRECEITA : null
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || mensagemErro);
    } finally {
      setMutating(false);
    }
  };

  const handleNovaReceita = async (paciente) => {
    if (!canMutate) {
      toast.warn('Apenas médicos podem criar receitas');
      return;
    }
    if (!paciente || !medico) {
      toast.warn('Dados do paciente ou médico não disponíveis');
      return;
    }

    await salvarDescricaoPendente();

    await criarReceita(
      {
        codConsulta: dadosPaciente?.CODCONSULTA ?? null,
        codMedico: medico.codMed,
        codPaciente: paciente?.CODPAC || codPac,
        tipo: tipoReceita,
        descricao: ehReceitaLivre(tipoReceita) ? '' : montarCabecalho(paciente),
      },
      'Nova receita criada com sucesso!',
      'Erro ao criar receita'
    );
  };

  const handleRepetirReceita = async (paciente) => {
    if (!canMutate || !medico) {
      toast.warn('Apenas médicos podem repetir receitas');
      return;
    }
    if (!receitaSelecionada) {
      toast.warn('Selecione uma receita para repetir');
      return;
    }

    await salvarDescricaoPendente();

    const livre = ehReceitaLivre(receitaSelecionada);
    const cabecalhoAtualizado = livre
      ? receitaSelecionada?.TEXTO || ''
      : `${montarCabecalho(paciente).replace(/\n\n\n$/, '\n\n')}${extrairCorpoReceita(receitaSelecionada?.TEXTO || '')}`;

    await criarReceita(
      {
        codConsulta: dadosPaciente?.CODCONSULTA ?? null,
        codMedico: medico.codMed,
        codPaciente: paciente?.CODPAC || codPac,
        tipo: receitaSelecionada.TIPORECEITA,
        descricao: cabecalhoAtualizado || receitaSelecionada?.TEXTO || '',
      },
      'Receita repetida com sucesso!',
      'Erro ao repetir receita'
    );
  };

  const handleSelecionarMedicamento = (medicamento) => {
    if (!canMutate || isEditLocked) return;
    const textoMedicamento = formatarMedicamentoParaReceita(medicamento, numeraReceita);
    atualizarDescricao(descricaoValueRef.current + textoMedicamento);
    toast.success(`${medicamento.nome} adicionado à receita!`);
  };

  const handleGravar = async (gravacao) => {
    if (!canMutate) {
      toast.warn('Apenas médicos podem gravar receitas');
      return;
    }
    if (!receitaSelecionada) {
      toast.warn('Nenhuma receita selecionada');
      return;
    }

    try {
      setMutating(true);
      await api.put(`receita/${receitaSelecionada.CODRECEITA}`, payloadUpdate(gravacao));
      setDirty(false);
      await mutate();
      toast.success('Receita gravada com sucesso!');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao gravar receita');
    } finally {
      setMutating(false);
    }
  };

  const handleExcluir = async () => {
    if (!canMutate) {
      toast.warn('Apenas médicos podem excluir receitas');
      return;
    }
    if (
      !receitaSelecionada ||
      !podeExcluirReceita(receitaSelecionada.GRAVACAO, receitaSelecionada.STATUS)
    ) {
      toast.warn('Esta receita não pode ser excluída');
      return;
    }

    const confirmar = window.confirm(
      `Excluir a receita #${receitaSelecionada.CODRECEITA}? Esta ação não pode ser desfeita.`
    );
    if (!confirmar) return;

    const cod = receitaSelecionada.CODRECEITA;
    try {
      setMutating(true);
      await deleteReceita(cod);
      setCodReceitaSelecionada(null);
      await mutate(
        (prev = []) =>
          Array.isArray(prev) ? prev.filter((item) => item.CODRECEITA !== cod) : prev,
        { revalidate: true }
      );
      toast.success('Receita excluída com sucesso.');
    } catch (err) {
      toast.error(mensagemErroExclusao(err, 'Erro ao excluir receita'));
    } finally {
      setMutating(false);
    }
  };

  const handleImprimir = async () => {
    if (!receitaSelecionada) {
      toast.warn('Selecione uma receita para imprimir');
      return;
    }

    await salvarDescricaoPendente();

    history.push({
      pathname: '/imprimir/receita',
      state: {
        receita: {
          ...receitaSelecionada,
          TIPORECEITA: tipoReceita,
          tipo: tipoReceita,
          TEXTO: ehReceitaLivre(tipoReceita)
            ? textoParaReceitaLivre(descricaoValueRef.current)
            : descricaoValueRef.current,
          NOMEMED: receitaSelecionada.NOMEMED || medico?.nome,
          CRMMED: receitaSelecionada.CRMMED || medico?.crm,
          ESPECIMED: receitaSelecionada.ESPECIMED || medico?.especialidade,
        },
        medico,
      },
    });
  };

  return {
    // SWR
    receitas,
    error,
    loading,
    mutate,
    erroInfraestrutura,
    temReceitas,
    // Estado derivado
    receitaSelecionada,
    isDefinitivo,
    isEditLocked,
    canMutate,
    numeraReceita,
    // Estado do formulário
    descricao,
    tipoReceita,
    usoTipo,
    setTipoReceita: alterarTipoReceita,
    setUsoTipo,
    setCodReceitaSelecionada,
    atualizarDescricao,
    salvarDescricaoPendente,
    // Handlers
    handleNovaReceita,
    handleRepetirReceita,
    handleSelecionarMedicamento,
    handleGravar,
    handleExcluir,
    handleImprimir,
  };
}
